// The player's hiscore name: chosen once (first visit prompt or Settings), changeable, remembered
// by this browser. The recovery key moves the name to another browser.

import { online, OnlineError } from '../online/client';
import { getFlag, setFlag } from '../storage/settings';

const $ = (id: string) => document.getElementById(id)!;
const DISMISSED = 'bhop-web-name-later';

/** Same checks as the server, loaded when first needed (the word list is a separate chunk). */
async function problem(name: string): Promise<{ name: string; problem: string | null }> {
  const m = await import('../online/names');
  const tidy = m.tidyName(name);
  return { name: tidy, problem: m.nameProblem(tidy) };
}

export class Account {
  constructor() {
    $('accSave').addEventListener(
      'click',
      () => void this.save(($('accName') as HTMLInputElement).value, 'accMsg'),
    );
    $('accName').addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') $('accSave').click();
    });
    $('promptSave').addEventListener(
      'click',
      () => void this.save(($('promptName') as HTMLInputElement).value, 'promptMsg'),
    );
    $('promptName').addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') $('promptSave').click();
    });
    $('promptLater').addEventListener('click', () => {
      setFlag(DISMISSED);
      this.render();
    });
    $('accCopyKey').addEventListener('click', () => {
      const t = online.me?.token;
      if (!t) return;
      void navigator.clipboard?.writeText(t).then(() => {
        $('accCopyKey').textContent = 'Copied';
        setTimeout(() => ($('accCopyKey').textContent = 'Copy recovery key'), 1500);
      });
    });
    $('accUseKey').addEventListener('click', async () => {
      const key = ($('accKeyInput') as HTMLInputElement).value.trim();
      if (!key) return;
      $('accMsg').textContent = 'Checking…';
      try {
        const i = await online.useKey(key);
        ($('accKeyInput') as HTMLInputElement).value = '';
        $('accMsg').textContent = `Welcome back, ${i.name}.`;
      } catch (e) {
        $('accMsg').textContent =
          e instanceof OnlineError && e.status === 401
            ? "That key doesn't belong to any player."
            : (e as Error).message;
      }
    });
    $('accSignOut').addEventListener('click', () => {
      if (
        !confirm(
          'Forget your name in this browser? Copy your recovery key first if you want to use the name again.',
        )
      )
        return;
      online.signOut();
      $('accMsg').textContent = '';
    });
    this.wirePopup();
    online.onChange(() => this.render());
    this.render();
    // Pick up a rename made in another browser.
    online.refresh().catch(() => {});
  }

  /** Save the name. 'ok', 'bad' (the name was refused) or 'offline' (the server couldn't be reached). */
  private async save(raw: string, msgId: string): Promise<'ok' | 'bad' | 'offline'> {
    const msg = $(msgId);
    const { name, problem: p } = await problem(raw);
    if (p) {
      msg.textContent = p;
      return 'bad';
    }
    msg.textContent = 'Saving…';
    try {
      if (online.me) {
        if (name !== online.me.name) await online.rename(name);
        msg.textContent = 'Saved.';
      } else {
        await online.signUp(name);
        msg.textContent = `Hi ${name}! Your times now go on the hiscores.`;
      }
      return 'ok';
    } catch (e) {
      msg.textContent = (e as Error).message;
      const status = e instanceof OnlineError ? e.status : 0;
      return status === 0 || status >= 500 ? 'offline' : 'bad';
    }
  }

  /** The popup's recovery key link: show where the key goes (Settings, Online). */
  onRecoveryKey: () => void = () => {};

  /** Waiting for the name popup (requireName). */
  private waiting: (() => void) | null = null;
  /** Played without a name this visit because the server couldn't be reached. */
  private offlineOk = false;

  /** A name is needed (new players), unless the server was out of reach this visit. */
  get needsName() {
    return !online.me && !this.offlineOk;
  }

  /**
   * The name popup: it can't be skipped. Resolves once the player has a name (or, only when the
   * hiscore server can't be reached, chose to play without one; the game must play offline).
   */
  requireName(why: string): Promise<void> {
    if (!this.needsName) return Promise.resolve();
    $('playerNameWhy').textContent = why;
    $('playerNameMsg').textContent = '';
    $('playerNameOffline').hidden = true;
    $('playerName').hidden = false;
    const input = $('playerNameInput') as HTMLInputElement;
    setTimeout(() => input.focus(), 0);
    return new Promise((resolve) => {
      const prev = this.waiting;
      this.waiting = () => {
        prev?.();
        resolve();
      };
    });
  }

  private finishPopup() {
    $('playerName').hidden = true;
    const done = this.waiting;
    this.waiting = null;
    done?.();
  }

  private wirePopup() {
    const submit = async () => {
      const result = await this.save(($('playerNameInput') as HTMLInputElement).value, 'playerNameMsg');
      if (result === 'ok') this.finishPopup();
      else if (result === 'offline') $('playerNameOffline').hidden = false;
    };
    $('playerNameOk').addEventListener('click', () => void submit());
    $('playerNameInput').addEventListener('keydown', (e) => {
      // Typing here never reaches the game.
      e.stopPropagation();
      if ((e as KeyboardEvent).key === 'Enter') void submit();
    });
    $('playerNameKey').addEventListener('click', () => {
      // Not a name yet: whatever waited for one is dropped (Play again once the key is in).
      $('playerName').hidden = true;
      this.waiting = null;
      this.onRecoveryKey();
    });
    $('playerNameOffline').addEventListener('click', () => {
      this.offlineOk = true;
      this.finishPopup();
    });
    // Clicks on it don't reach the game or the builder.
    $('playerName').addEventListener('mousedown', (e) => e.stopPropagation());
  }

  render() {
    const me = online.me;
    $('accStatus').textContent = me ? me.name : 'not set';
    const input = $('accName') as HTMLInputElement;
    if (document.activeElement !== input) input.value = me?.name ?? '';
    $('accSave').textContent = me ? 'Change name' : 'Save name';
    $('accKeyBox').hidden = !me;
    $('accSignOut').hidden = !me;
    $('namePrompt').hidden = !!me || getFlag(DISMISSED);
  }
}
